import React, { useRef } from 'react';
import { useScroll, useTransform, motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Eye, LayoutDashboard, Video } from 'lucide-react';

const benefits = [
  {
    icon: Eye,
    title: 'Visibilidade',
    description: 'Melhore a visibilidade do seu negócio nas redes sociais e alcance mais clientes potenciais.',
  },
  {
    icon: LayoutDashboard,
    title: 'CRM',
    description: 'Organize o seu negócio com um de nossos sistemas de gestão inteligente.',
  },
  {
    icon: Video,
    title: 'Edição de Vídeo',
    description: 'Tenha seu vídeo gravado e editado profissionalmente para impactar sua audiência.',
  },
];

export function BenefitsStickyScroll() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // Phases
  // 0 - 0.4: Show "Por Que Escolher..." and Image
  // 0.4 - 0.6: Transition (Image fades out? or moves? User said "começar crescendo de fundo essa mesma pergunta... com a imagem de fundo... depois aparece esses 3 quadrados")
  // 0.6 - 1.0: Show "Queremos ser..." and Cards
  
  // Opacity for Phase 1 (Intro)
  const introOpacity = useTransform(scrollYProgress, [0, 0.4, 0.5], [1, 1, 0]);
  const introScale = useTransform(scrollYProgress, [0, 0.4], [0.8, 1]); // Expand effect "crescendo"

  // Opacity for Phase 2 (Cards)
  const contentOpacity = useTransform(scrollYProgress, [0.4, 0.5, 1], [0, 1, 1]);
  const contentY = useTransform(scrollYProgress, [0.4, 0.5], [50, 0]);

  return (
    <div ref={containerRef} className="relative h-[300vh] bg-white">
      <div className="sticky top-0 h-screen overflow-hidden flex flex-col items-center justify-center">
        
        {/* Phase 1: Intro with Background Image */}
        <motion.div 
          style={{ opacity: introOpacity, scale: introScale }}
          className="absolute inset-0 flex items-center justify-center z-10"
        >
          {/* Background Image Layer */}
          <div className="absolute inset-0 z-0">
             <img 
               src="/pensando.png" 
               alt="Pensando" 
               className="w-full h-full object-cover opacity-30" // Lower opacity to make text readable? User said "imagem de fundo".
             />
             <div className="absolute inset-0 bg-white/50 backdrop-blur-[2px]"></div> {/* Overlay for readability */}
          </div>

          <h2 className="relative z-10 text-5xl md:text-7xl font-bold text-center text-[#1E1B4B] px-4">
            Por Que Escolher a VitaCode?
          </h2>
        </motion.div>

        {/* Phase 2: Content Cards */}
        <motion.div 
          style={{ opacity: contentOpacity, y: contentY }}
          className="absolute inset-0 flex flex-col items-center justify-center z-20 bg-white" // Solid background to cover the previous image
        >
          <div className="max-w-7xl mx-auto px-6 w-full">
            <h2 className="text-3xl md:text-5xl font-bold text-center text-[#1E1B4B] mb-16 max-w-4xl mx-auto">
              Queremos ser a maior empresa de Campo Grande nas seguintes áreas
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <Card key={index} className="border-2 border-transparent hover:border-[#1E1B4B]/20 transition-all duration-300 shadow-xl bg-white">
                    <CardContent className="p-8 text-center flex flex-col items-center">
                      <div className="w-20 h-20 bg-[#FDF2F8] rounded-full flex items-center justify-center mb-6 shadow-sm">
                        <Icon className="w-10 h-10 text-[#1E1B4B]" />
                      </div>
                      <h3 className="text-2xl font-bold text-[#1E1B4B] mb-4">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed text-lg">
                        {benefit.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
}
